import { useEffect, useState } from 'react';
import { ArrowLeft, Loader2, ShieldCheck, Bell } from 'lucide-react';
import { Link } from 'react-router';
import logo from '@/assets/599ad72d49c8887573a4750887df95ce60e36150.png';
import { settingsService } from '../services/settingsService';

const EXPLICIT_CONSENT_KEY = 'driver_explicit_consent_text';
const ELECTRONIC_CONSENT_KEY = 'driver_electronic_consent_text';

const DEFAULT_MESSAGE = 'Metin henüz tanımlanmamış. Lütfen yönetim panelinden güncelleyin.';

interface ConsentPageProps {
  settingKey: string;
  title: string;
  badgeLabel: string;
  badgeClassName: string;
  icon: 'explicit' | 'electronic';
}

function ConsentPageContent({ settingKey, title, badgeLabel, badgeClassName, icon }: ConsentPageProps) {
  const [content, setContent] = useState(DEFAULT_MESSAGE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    settingsService
      .getPublicSettingValue<string>(settingKey, DEFAULT_MESSAGE)
      .then((value) => {
        if (!active) return;
        setContent(String(value || DEFAULT_MESSAGE));
      })
      .catch(() => {
        if (!active) return;
        setContent(DEFAULT_MESSAGE);
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [settingKey]);

  const Icon = icon === 'explicit' ? ShieldCheck : Bell;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto max-w-5xl space-y-8 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm md:p-10">
        <div className="flex items-center justify-between gap-4">
          <Link to="/driver-mobile-auth" className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200">
            <ArrowLeft className="h-4 w-4" /> Geri Dön
          </Link>
          <img src={logo} alt="Yük Ortağım Logo" className="h-14 w-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-sm" />
        </div>

        <div>
          <div className={`mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium ${badgeClassName}`}>
            <Icon className="h-4 w-4" /> {badgeLabel}
          </div>
          <h1 className="text-3xl font-bold text-slate-900">{title}</h1>
        </div>

        <section className="space-y-5 leading-7 text-slate-700">
          {loading ? (
            <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-slate-600">
              <Loader2 className="h-5 w-5 animate-spin" /> Metin yükleniyor...
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm leading-7 text-slate-700 whitespace-pre-wrap">
              {content}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export function ExplicitConsent() {
  return (
    <ConsentPageContent
      settingKey={EXPLICIT_CONSENT_KEY}
      title="Doğuş Yük Ortağım – Sürücü Arka Plan Konum Verisi Açık Rıza Metni"
      badgeLabel="KVKK – Açık Rıza"
      badgeClassName="border border-purple-200 bg-purple-50 text-purple-800"
      icon="explicit"
    />
  );
}

export function ElectronicConsent() {
  return (
    <ConsentPageContent
      settingKey={ELECTRONIC_CONSENT_KEY}
      title="Yük Ortağım Sürücü Ticari Elektronik İleti Onay Metni"
      badgeLabel="Ticari Elektronik İleti"
      badgeClassName="border border-teal-200 bg-teal-50 text-teal-800"
      icon="electronic"
    />
  );
}